import { useEffect, useMemo, useState } from 'react';
import { Loader2, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { API_BASE } from '../../utils/api-base';
import { bridgeName, useClientBridges } from './BridgePicker';

// Assign many devices to a bridge PC at once, grouped by the bus they sit on
// ("COM3 · 27 devices"). VFDs, controllers and IO modules of the client are all listed,
// so one pass sets a whole panel no matter which admin tab it is opened from.

type DeviceRow = {
  kind: 'vfd' | 'controller' | 'io'; kindLabel: string; id: string; name: string;
  group: string; detail: string; bridgeId: string | null; isActive: boolean;
};

const keyOf = (d: DeviceRow) => `${d.kind}:${d.id}`;

export function BulkBridgeAssign({ token, clientId, onDone }: { token: string; clientId: string; onDone?: () => void }) {
  const bridges = useClientBridges(token, clientId);
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/clients/${encodeURIComponent(clientId)}/device-bridges`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.error || 'Could not load devices');
      setDevices(d.devices || []);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  useEffect(() => { if (!target && bridges.length) setTarget(bridges[0].bridgeId); }, [bridges, target]);

  const groups = useMemo(() => {
    const m = new Map<string, DeviceRow[]>();
    for (const d of devices) m.set(d.group, [...(m.get(d.group) || []), d]);
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [devices]);

  const toggle = (keys: string[], on: boolean) => setSelected((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
    return next;
  });

  const apply = async () => {
    const chosen = devices.filter((d) => selected.has(keyOf(d)));
    if (chosen.length === 0) { toast.error('Tick the devices to assign first'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/clients/${encodeURIComponent(clientId)}/device-bridges`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ bridgeId: target || null, devices: chosen.map((d) => ({ kind: d.kind, id: d.id })) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.error || 'Could not assign devices');
      toast.success(`${d.updated} device(s) assigned to ${bridgeName(bridges, target || null) || 'any bridge PC'}`, {
        description: 'Each bridge picks up the change within about a minute.',
      });
      setSelected(new Set());
      await load();
      onDone?.();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} disabled={!clientId}>
        <Monitor className="mr-2 h-4 w-4" /> Assign bridge PCs
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col">
          <DialogHeader>
            <DialogTitle>Assign devices to bridge PCs</DialogTitle>
            <DialogDescription>
              Tick a whole COM port or single devices, choose the PC they are wired to and apply. A device on a PC is read
              only by that PC; the client&apos;s other bridge PCs leave it alone.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading devices…</div>
            ) : groups.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">This client has no VFDs, controllers or IO modules yet.</p>
            ) : groups.map(([group, rows]) => {
              const keys = rows.map(keyOf);
              const all = keys.every((k) => selected.has(k));
              const some = !all && keys.some((k) => selected.has(k));
              return (
                <div key={group} className="rounded-lg border">
                  <label className="flex cursor-pointer items-center gap-3 border-b bg-muted/40 px-3 py-2">
                    <input type="checkbox" className="h-4 w-4" checked={all} ref={(el) => { if (el) el.indeterminate = some; }}
                      onChange={(e) => toggle(keys, e.target.checked)} />
                    <span className="font-semibold">{group}</span>
                    <span className="text-xs text-muted-foreground">{rows.length} device{rows.length === 1 ? '' : 's'}</span>
                  </label>
                  <div className="divide-y">
                    {rows.map((d) => (
                      <label key={keyOf(d)} className="flex cursor-pointer items-center gap-3 px-3 py-1.5 text-sm hover:bg-muted/30">
                        <input type="checkbox" className="h-4 w-4" checked={selected.has(keyOf(d))} onChange={(e) => toggle([keyOf(d)], e.target.checked)} />
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-medium">{d.name}</span>
                          <span className="text-muted-foreground"> · {d.kindLabel} · {d.detail}{d.isActive ? '' : ' · inactive'}</span>
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">{bridgeName(bridges, d.bridgeId) || 'Any bridge PC'}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t pt-3">
            <span className="text-sm text-muted-foreground">{selected.size} selected → assign to</span>
            <select value={target} onChange={(e) => setTarget(e.target.value)}
              className="h-9 min-w-[16rem] flex-1 rounded-md border border-input bg-input-background px-3 text-sm">
              {bridges.map((b) => <option key={b.bridgeId} value={b.bridgeId}>PC {b.mac || b.bridgeId}</option>)}
              <option value="">Any bridge PC of this client</option>
            </select>
            <Button onClick={apply} disabled={saving || selected.size === 0}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
